/**
 * The setup guide, drawn once for both sides (2026-09-30; a player since
 * 2026-10-14).
 *
 * /guides, /guides/ios and /guides/android keep the server HTML on first
 * paint. Both sides call these functions with the same `guides` content: the
 * i18n bundle on the client, data/guides-content.json (derived from it) on the
 * server. Until 2026-09-30 the client drew its own stepper with emoji
 * mock-ups, so one URL showed two pages; that is why there is one renderer.
 *
 * A device page is a step-by-step player, one tap per step, after the Quantek
 * install guide (operator, 2026-10-14). Each install method (tap the link,
 * scan the QR from a second screen, enter the code) is its own sequence; the
 * phone's setup questions and "turn on data" are shared by all of them. Every
 * step carries a drawn phone screen in the reader's language.
 *
 * The server writes every step into the page as an ordered list (what search
 * engines, the markdown view and readers without JS get), plus the player
 * chrome showing the first step. `guidePlayer()` turns it into the player: an
 * inline copy runs right after the markup on first paint, and the client
 * route calls the same function after an in-app navigation.
 */
import { renderGuideSections } from './guide-sections.js';
import { guideEsc, renderGuidePhone } from './guide-screens.js';
import { guidePlayer } from './guide-player.js';
export const GUIDE_PLAN = {
    ios: {
        methods: ['tap', 'scan', 'manual'],
        phone: 'tap',
        desk: 'scan',
        get: { tap: ['showQrHere'], scan: ['showQrThere', 'qrOnScreen'], manual: ['showQrHere', 'copyCode'] },
        install: {
            tap: ['tapInstall', 'activate'],
            scan: ['openCellular', 'addEsim', 'useQr', 'scanQr', 'activate'],
            manual: ['openCellular', 'addEsim', 'useQr', 'enterManually', 'pasteCode', 'activate'],
        },
        finish: ['labels', 'defaultLine', 'imessage', 'cellData'],
        data: ['dataCellular', 'dataRow', 'pickTravel', 'travelLine', 'roaming'],
    },
    android: {
        methods: ['manual', 'scan'],
        phone: 'manual',
        desk: 'scan',
        get: { scan: ['showQrThere', 'qrOnScreen'], manual: ['showQrHere', 'copyCode'] },
        install: {
            scan: ['openConnections', 'simManager', 'addEsim', 'scanQr', 'aimCamera', 'addConfirm'],
            manual: ['openConnections', 'simManager', 'addEsim', 'scanQr', 'enterManually', 'pasteCode', 'download'],
        },
        finish: [],
        data: ['dataConnections', 'dataSimManager', 'mobileData', 'mobileNetworks', 'roaming'],
    },
};
/** One method's whole sequence: [step id, part] in the order the player shows them. */
export function guideSequence(device, method) {
    const p = GUIDE_PLAN[device];
    return [
        ...(p.get[method] ?? []).map((id) => [id, 'get']),
        ...(p.install[method] ?? []).map((id) => [id, 'install']),
        ...p.finish.map((id) => [id, 'install']),
        ...p.data.map((id) => [id, 'data']),
    ];
}
/** Step copy carries <strong>/<em> and &amp;; nothing else is admitted. */
function rich(s) {
    return guideEsc(s.replace(/&amp;/g, '&'))
        .replace(/&lt;(\/?)(strong|em)&gt;/g, '<$1$2>');
}
/**
 * Text for a label set in capitals: brand names keep English casing rules.
 * Under lang="tr", text-transform turns the i of iPhone, iOS and Android into
 * a dotted capital (İOS, ANDROİD); marking them lang="en" keeps them right.
 */
function caps(s) {
    return guideEsc(s).replace(/\b(iPhone|iOS|Android)\b/g, '<span lang="en">$1</span>');
}
// The dashboard is not a localized SEO path: links to it stay bare, like the
// trial CTA's, and the router adds the reader's language on click.
/** Support channels, as the FAQ and the dashboard name them. */
const SUPPORT_BOT = 'https://t.me/roamzy_support_bot';
const SUPPORT_MAIL = 'support@roamzy.io';
// ─── Steps ─────────────────────────────────────────────────────────────────
function stepText(step, num, c) {
    return `<div class="gp-step__text">
        <span class="gp-step__num">${String(num).padStart(2, '0')}</span>
        <h4 class="gp-step__title">${rich(step.title)}</h4>
        <p class="gp-step__body">${rich(step.body)}</p>
        ${step.tip ? `<p class="gp-step__tip"><span class="gp-step__tip-label">${guideEsc(c.player.tip)}</span> ${rich(step.tip)}</p>` : ''}
      </div>`;
}
function stepItem(id, part, num, device, content, o) {
    const flow = content[device];
    const step = flow.steps[id];
    const screen = step ? flow.screens[step.screen] : undefined;
    if (!step || !screen)
        return '';
    return `<li class="gp-step" data-step="${guideEsc(id)}" data-part="${part}">
      ${stepText(step, num, content.manual)}
      ${renderGuidePhone(screen, device, content.manual, o)}
    </li>`;
}
/** A block of steps: one method's own steps, or the steps every method shares. */
function block(attrs, title, sub, items, device, content, o) {
    const steps = items.map(([id, part], i) => stepItem(id, part, i + 1, device, content, o)).join('');
    return `<section class="gp-block" ${attrs}>
      <h3 class="gp-block__title">${guideEsc(title)}${sub ? ` <span class="gp-block__when">${caps(sub)}</span>` : ''}</h3>
      <ol class="gp-steps">${steps}</ol>
    </section>`;
}
const PART_NUM = { get: '01', install: '02', data: '03' };
/** The list of all steps for one method, grouped by part: the player marks the current one. */
function board(device, method, content, active) {
    const c = content.manual;
    const seq = guideSequence(device, method);
    let n = 0;
    const parts = ['get', 'install', 'data'].map((part) => {
        const rows = seq.filter(([, p]) => p === part).map(([id]) => {
            const title = content[device].steps[id]?.title ?? '';
            const i = n++;
            return `<li><button type="button" class="gp-row${i === 0 ? ' is-now' : ''}" data-i="${i}"${i === 0 ? ' aria-current="step"' : ''}><span class="gp-row__n">${String(i + 1).padStart(2, '0')}</span><span class="gp-row__t">${rich(title).replace(/<\/?(strong|em)>/g, '')}</span><span class="gp-row__s">${i === 0 ? guideEsc(c.player.now) : ''}</span></button></li>`;
        }).join('');
        return rows ? `<li class="gp-board__part"><span class="gp-board__head">${PART_NUM[part]} · ${guideEsc(c.parts[part])}</span><ol class="gp-board__rows">${rows}</ol></li>` : '';
    }).join('');
    return `<ol class="gp-board__list" data-method="${method}"${active ? '' : ' hidden'}>${parts}</ol>`;
}
/**
 * A browser that cannot run the player (a script error, or syntax older than
 * the build targets) would be left on its first step with the list hidden.
 * Plain ES5, separate from the player's own script: after 2.5 s without a
 * ready player, the page drops back to the list.
 */
const PLAYER_FALLBACK = "setTimeout(function(){var g=document.getElementById('steps');if(g&&!g.gpReady)g.className=g.className.replace(' gp--live','')},2500)";
/**
 * The player. Its chrome shows the first step of the method that opens on a
 * phone; `guidePlayer()` then picks the reader's method and wires it up. The
 * steps themselves sit in `.gp-flow`, in document order.
 */
function player(device, content, o) {
    const c = content.manual;
    const flow = content[device];
    const plan = GUIDE_PLAN[device];
    const first = plan.phone;
    const methods = plan.methods.filter((m) => flow.methods[m]);
    const tabs = methods.map((m) => {
        const mc = flow.methods[m];
        const on = m === first;
        return `<button type="button" role="tab" class="gp-tab" id="gp-tab-${m}" data-method="${m}" aria-controls="gp-panel" aria-selected="${on}"${on ? '' : ' tabindex="-1"'}><span class="gp-tab__label">${guideEsc(mc.label)}</span><span class="gp-tab__when">${caps(mc.when)}</span></button>`;
    }).join('');
    const hints = methods.map((m) => `<p class="gp-hint" data-method="${m}" data-md="skip"${m === first ? '' : ' hidden'}>${guideEsc(flow.methods[m].hint)}</p>`).join('');
    // A method's own steps are its get and install lists, in that order.
    const blocks = methods.map((m, i) => {
        const mc = flow.methods[m];
        const items = [
            ...(plan.get[m] ?? []).map((id) => [id, 'get']),
            ...(plan.install[m] ?? []).map((id) => [id, 'install']),
        ];
        return block(`data-method="${m}"`, `${String.fromCharCode(65 + i)} · ${mc.label}`, mc.when, items, device, content, o);
    }).join('');
    const shared = (plan.finish.length ? block('data-shared="finish"', c.finish, '', plan.finish.map((id) => [id, 'install']), device, content, o) : '')
        + block('data-shared="data"', `${PART_NUM.data} · ${c.parts.data}`, '', plan.data.map((id) => [id, 'data']), device, content, o);
    // The first step, as the player shows it before guidePlayer() runs.
    const seq = guideSequence(device, first);
    const [firstId, firstPart] = seq[0] ?? ['', 'get'];
    const firstStep = flow.steps[firstId];
    const firstScreen = firstStep ? flow.screens[firstStep.screen] : undefined;
    // One segment per part; guidePlayer() fills them as the reader moves.
    const segs = ['get', 'install', 'data']
        .map((p) => `<span class="gp-seg" data-part="${p}"><span class="gp-seg__fill"></span></span>`).join('');
    const partAttrs = ['get', 'install', 'data']
        .map((p) => `data-part-${p}="${guideEsc(`${PART_NUM[p]} · ${c.parts[p]}`)}" data-sub-${p}="${guideEsc(c.partSub[p])}"`).join(' ');
    return `<section class="gp gp--live" id="steps" data-device="${device}" data-phone="${plan.phone}" data-desk="${plan.desk}" data-l-now="${guideEsc(c.player.now)}" ${partAttrs} aria-labelledby="steps-title">
    <h2 class="gp-sr" id="steps-title">${guideEsc(c.player.title)}</h2>
    <div class="gp-tabs" role="tablist" aria-label="${guideEsc(c.player.methods)}">${tabs}</div>
    ${hints}
    <div class="gp-panel" id="gp-panel" role="tabpanel" tabindex="0" aria-labelledby="gp-tab-${first}" data-md="skip">
      <div class="gp-main">
        <div class="gp-meta">
          <span class="gp-part">${guideEsc(`${PART_NUM[firstPart]} · ${c.parts[firstPart]}`)}</span>
          <span class="gp-count"><span class="gp-count__n">01</span><span class="gp-count__of">/ <span class="gp-count__t">${String(seq.length).padStart(2, '0')}</span></span></span>
        </div>
        <p class="gp-sub">${guideEsc(c.partSub[firstPart])}</p>
        <div class="gp-words">
          <div class="gp-say" aria-live="polite">${firstStep ? stepText(firstStep, 1, c) : ''}</div>
          <div class="gp-tip"></div>
        </div>
        <div class="gp-nav">
          <div class="gp-segs" aria-hidden="true">${segs}</div>
          <div class="gp-buttons">
            <button type="button" class="btn btn--outline gp-prev" aria-disabled="true">${guideEsc(c.player.back)}</button>
            <button type="button" class="btn btn--accent gp-next">${guideEsc(c.player.next)}</button>
          </div>
          <p class="gp-keys" aria-hidden="true"><kbd>←</kbd><kbd>→</kbd> ${guideEsc(c.player.keys)}</p>
        </div>
      </div>
      <div class="gp-phone">${firstScreen ? renderGuidePhone(firstScreen, device, c, o) : ''}</div>
      <nav class="gp-board" aria-label="${guideEsc(c.player.board)}">
        <span class="gp-board__title">${guideEsc(c.player.board)}</span>
        ${methods.map((m) => board(device, m, content, m === first)).join('')}
      </nav>
    </div>
    <div class="gp-flow">${blocks}${shared}</div>
  </section>
  <script>(${guidePlayer.toString()})(document.getElementById('steps'))</script>
  <script>${PLAYER_FALLBACK}</script>
  <noscript><style>.gp--live .gp-tabs,.gp--live .gp-hint,.gp--live .gp-panel{display:none}.gp--live .gp-flow{display:block}</style></noscript>`;
}
// ─── Pages ─────────────────────────────────────────────────────────────────
function trail(o, hub, current) {
    const items = [`<a href="${guideEsc(o.href('/'))}">${guideEsc(o.home)}</a>`];
    if (hub)
        items.push(`<a href="${guideEsc(o.href('/guides'))}">${guideEsc(hub)}</a>`);
    if (current)
        items.push(`<span>${guideEsc(current)}</span>`);
    return `<div class="breadcrumbs">${items.join(' › ')}</div>`;
}
/** Hub title as a trail item: a display headline ("Setup, illustrated.") without its stop. */
function hubCrumb(content) {
    return (content.hero.title ?? 'Guides').replace(/[.。]$/, '');
}
/** iPhone / Android: the other device is a link to its own page. */
function osSwitch(content, device, o) {
    const item = (d) => d === device
        ? `<span class="gp-os__item" aria-current="page">${caps(content.tabs[d])}</span>`
        : `<a class="gp-os__item" href="${guideEsc(o.href(`/guides/${d}`))}">${caps(content.tabs[d])}</a>`;
    return `<nav class="gp-os" aria-label="${guideEsc(content.manual.player.os)}">${item('ios')}${item('android')}</nav>`;
}
/** Before the steps: the free eSIM, a connection, install at home. */
function before(content) {
    const c = content.manual;
    const items = c.before.map((b, i) => `<li class="gp-before__item">
      <span class="gp-before__num">${String(i + 1).padStart(2, '0')}</span>
      <h3 class="gp-before__title">${guideEsc(b.title)}</h3>
      <p class="gp-before__body">${rich(b.body)}</p>
      ${i === 0 ? `<p class="gp-before__cta"><a class="btn btn--accent" href="/dashboard">${guideEsc(c.ui.cta)}</a><span class="manual-step__note">${guideEsc(c.get.note)}</span></p>` : ''}
    </li>`).join('');
    return `<ol class="gp-before">${items}</ol>`;
}
function support(c) {
    return `<aside class="gp-support" aria-labelledby="gp-support-title">
      <div class="section__kicker">${guideEsc(c.support.kicker)}</div>
      <h2 class="gp-support__title" id="gp-support-title">${guideEsc(c.support.title)}</h2>
      <p class="gp-support__body">${guideEsc(c.support.body)}</p>
      <p class="gp-support__links">
        <a class="btn btn--accent" href="${SUPPORT_BOT}" rel="noopener">${guideEsc(c.support.telegram)}</a>
        <a class="btn btn--outline" href="mailto:${SUPPORT_MAIL}">${guideEsc(c.support.email)}</a>
      </p>
    </aside>`;
}
/** /guides/ios, /guides/android. */
export function renderGuideDevicePage(content, device, o) {
    const c = content.manual;
    const other = device === 'ios' ? 'android' : 'ios';
    return `<article class="guides-route guides-route--${device} manual" data-ssr="1" data-device="${device}">
  <header class="manual-hero">
    <div class="container">
      ${trail(o, hubCrumb(content), content.tabs[device])}
      <div class="manual-hero__grid">
        <div class="manual-hero__text">
          <div class="section__kicker">${caps(content.tabs[device])} · ${caps(c.time)}</div>
          <h1 class="article-h1 manual-hero__title">${guideEsc(c.h1[device])}</h1>
          <p class="article-lede manual-hero__lede">${guideEsc(c.lede[device])}</p>
        </div>
        ${osSwitch(content, device, o)}
      </div>
      ${before(content)}
    </div>
  </header>
  <div class="container manual-body">
    ${player(device, content, o)}
    <div class="gp-after">
      <section class="manual-done">
        <h2 class="manual-done__title">${guideEsc(content.complete.title ?? '')}</h2>
        <p class="manual-done__body">${guideEsc(content.complete.body ?? '')}</p>
        <p class="manual-done__links">
          <a class="btn btn--outline" href="/dashboard">${guideEsc(content.complete.cta ?? '')}</a>
          <a class="manual-link" href="${guideEsc(o.href(`/guides/${other}`))}">${guideEsc(c.other[device])}</a>
          <a class="manual-link" href="${guideEsc(o.href('/guides'))}">${guideEsc(c.allGuides)}</a>
        </p>
      </section>
      ${support(c)}
    </div>
  </div>
  <section class="guides-notes-section">
    <div class="container">${renderGuideSections(content.sections?.[device])}</div>
  </section>
</article>`;
}
function partHead(num, id, title, lede) {
    return `<header class="manual-part__head">
      <span class="manual-part__num" aria-hidden="true">${num}</span>
      <h2 class="manual-part__title" id="${id}-title">${guideEsc(title)}</h2>
      ${lede ? `<p class="manual-part__lede">${guideEsc(lede)}</p>` : ''}
    </header>`;
}
/** /guides: the free eSIM first, with the dashboard card it lands in. */
function getPart(c, o) {
    return `<section class="manual-part" id="get" aria-labelledby="get-title">
    ${partHead('01', 'get', c.parts.get)}
    <ol class="manual-steps manual-steps--single">
      <li class="manual-step">
        <div class="manual-step__text">
          <h3 class="manual-step__title">${guideEsc(c.get.title)}</h3>
          <p class="manual-step__body">${rich(c.get.body)}</p>
          <p class="manual-step__cta"><a class="btn btn--accent" href="/dashboard">${guideEsc(c.ui.cta)}</a><span class="manual-step__note">${guideEsc(c.get.note)}</span></p>
        </div>
        ${renderGuidePhone({ kind: 'card' }, 'ios', c, o)}
      </li>
    </ol>
  </section>`;
}
function deviceCard(content, device, o) {
    const c = content.manual;
    const flow = content[device];
    const methods = GUIDE_PLAN[device].methods.filter((m) => flow.methods[m]);
    const rows = methods.map((m, i) => {
        const mc = flow.methods[m];
        return `<li><span class="manual-toc__num">${String.fromCharCode(65 + i)}</span>${guideEsc(mc.label)} <em>${guideEsc(mc.when)}</em></li>`;
    }).join('');
    return `<a class="manual-device" href="${guideEsc(o.href(`/guides/${device}`))}">
      <span class="manual-device__name">${guideEsc(content.tabs[device])}</span>
      <span class="manual-device__time">${guideEsc(c.time)}</span>
      <ol class="manual-device__plan">
        ${rows}
        <li><span class="manual-toc__num">${PART_NUM.data}</span>${guideEsc(c.parts.data)}</li>
      </ol>
      <span class="manual-device__go">${guideEsc(c.open[device])} →</span>
    </a>`;
}
function pwaBlock(content) {
    const p = content.pwa;
    if (!p)
        return '';
    const list = (steps) => Object.keys(steps ?? {}).sort((a, b) => Number(a) - Number(b))
        .map((k) => `<li>${rich((steps ?? {})[k] ?? '')}</li>`).join('');
    return `<section class="manual-pwa" id="pwa">
      <div class="manual-pwa__head">
        ${p.kicker ? `<div class="section__kicker">${guideEsc(p.kicker)}</div>` : ''}
        <h2 class="manual-part__title">${guideEsc(p.title ?? '')}</h2>
        <p class="manual-part__lede">${guideEsc(p.lede ?? '')}</p>
      </div>
      <div class="manual-pwa__cols">
        <div><h3 class="manual-pwa__os">${guideEsc(p.tabs?.ios ?? content.tabs.ios)}</h3><ol class="manual-pwa__steps">${list(p.ios)}</ol></div>
        <div><h3 class="manual-pwa__os">${guideEsc(p.tabs?.android ?? content.tabs.android)}</h3><ol class="manual-pwa__steps">${list(p.android)}</ol></div>
      </div>
      ${p.tip ? `<p class="manual-pwa__tip">${rich(p.tip)}</p>` : ''}
      ${p.installCta ? `<button type="button" class="btn btn--accent pwa-install-btn" data-pwa-install>${guideEsc(p.installCta)}</button>` : ''}
    </section>`;
}
/** /guides. */
export function renderGuideHubPage(content, o) {
    const c = content.manual;
    return `<article class="guides-route guides-route--index manual manual--hub" data-ssr="1">
  <header class="manual-hero">
    <div class="container">
      ${trail(o, null, null)}
      <div class="manual-hero__text">
        ${content.hero.kicker ? `<div class="section__kicker">${guideEsc(content.hero.kicker)}</div>` : ''}
        <h1 class="article-h1 manual-hero__title">${guideEsc(content.hero.title ?? '')}</h1>
        <p class="article-lede manual-hero__lede">${guideEsc(content.hero.lede ?? '')}</p>
      </div>
    </div>
  </header>
  <div class="container manual-body">
    <div class="manual-flow">
      ${getPart(c, o)}
      <section class="manual-part" id="install" aria-labelledby="install-title">
        ${partHead('02', 'install', c.parts.install)}
        <div class="manual-devices">
          ${deviceCard(content, 'ios', o)}
          ${deviceCard(content, 'android', o)}
        </div>
      </section>
      ${pwaBlock(content)}
    </div>
  </div>
</article>`;
}
/**
 * The same steps as plain text, for the HowTo JSON-LD: the structured data
 * describes the page it sits on, in the page's language. The install methods
 * are alternatives, so each is its own section (with the setup questions every
 * method ends with); turning on data is the last one.
 */
export function guideHowToSections(content, device) {
    const strip = (s) => s.replace(/<[^>]+>/g, '').replace(/&amp;/g, '&');
    const c = content.manual;
    const flow = content[device];
    const plan = GUIDE_PLAN[device];
    const steps = (ids) => ids.map((id) => flow.steps[id]).filter((s) => !!s)
        .map((s) => ({ name: strip(s.title), text: strip(s.body) }));
    return [
        ...plan.methods.filter((m) => flow.methods[m]).map((m) => ({
            name: `${c.parts.install}: ${flow.methods[m].label}`,
            steps: steps([...(plan.get[m] ?? []), ...(plan.install[m] ?? []), ...plan.finish]),
        })),
        { name: c.parts.data, steps: steps(plan.data) },
    ];
}
